// What a drag on the board means (PLAN-UI §28.3, U5; screen-mock `#rail`).
//
// Dragging has three meanings — move the view, draw a lane, lift one — and the
// game used to pick between them from what was under the pointer: press on
// ground you hold and you were drawing, press anywhere else and you were moving
// the camera. That heuristic is right about intent most of the time and gives
// no way to *say* otherwise, so the two cases it got wrong had no recourse. So
// the mode is a control, and this is the model behind it.
//
// **The camera is no longer one of the tools.** Panning and turning were a tile
// each with shift swapping them, which made the commonest thing on the board —
// moving the view — modal: the same drag did two different things depending on
// what was lit, and a wobbly click left the board askew. A drag nothing else
// claims now pans, shift-drag turns, and the camera's discrete actions are the
// group of tiles under these (`viewActions.ts`).
//
// **The model is pure and the rail draws it**, the shape `hotbar.ts` set: what
// is offered, what is lit and what is refused is arithmetic a test can hold,
// and the component is a `map` over the answer.

import type { IconName } from "./kit";

/** What a drag does. Not a mode over clicks — a click still means what it did. */
export type Tool = "turn" | "build" | "erase";

/**
 * What each tool does, said once. The rail's titles and — until U7's pill —
 * the strip's caption line both read it, so the sentence cannot drift between
 * the button and the line explaining the button.
 */
export const TOOL_HINT: Record<string, string> = {
  turn: "drag to move the view · shift-drag to turn it",
  // Both gestures, because this tool is now the only place either of them
  // works: clicking two ends used to lay a lane whatever tool was up.
  build: "drag along your ground to lay a lane · or click its two ends",
  // A building is a click and never a sweep, because taking one down spends
  // the turn and lifting track does not (`interaction/discard.ts`). **The cost
  // is said here** because this card is the only place left to say it before
  // the click: the inspector's button, whose card carried it, is gone.
  // Short, because the pill says it too on a tool change.
  erase: "drag over your parts to take them up · click a building to take it down — that costs the turn"
};

/** An entry as authored: a picture, a word, and the sentence under it. */
interface Entry {
  id: Tool;
  label: string;
  /** A kit icon, where the set draws one. */
  icon?: IconName;
  /** Otherwise a glyph. Nothing uses it now that the set draws the pawn. */
  glyph?: string;
}

/**
 * The three tiles, in the mock's order.
 *
 * **Named for what they act on, not for what the drag does.** "Turn" describes
 * the gesture, which is the one thing already obvious once you are dragging;
 * what a player is choosing between is *the pieces* and *the floor*. Build
 * keeps its name because laying track is the subject and the verb at once.
 *
 * **Its opposite is Discard, and it is universal.** It was "Lift" while track
 * was the only thing it took; a building came down from a button in its own
 * inspector, so getting rid of a thing had two doors depending on the thing.
 * One tool now takes whatever of yours can come off the board, and the id
 * stays `erase` because `data-testid` is API.
 *
 * **Discard is a tile, not a context of Build.** The strip had made the pair a
 * sub-row that only existed once you were building, on the argument that they
 * are one job asked twice. That is true and it cost a click on the rarer half
 * every time, and it made the row's width depend on the live tool.
 *
 * The Camera tile went with the mode: the view moves under any tool now.
 */
const ENTRIES: readonly Entry[] = [
  // The kit's pawn, not the ♟ glyph: a system font can claim U+265F and draw
  // nothing (`PIECE_ICON` in `glyphs.ts` tells the story).
  { id: "turn", label: "Pieces", icon: "pawn" },
  { id: "build", label: "Build", icon: "track" },
  { id: "erase", label: "Discard", icon: "lift" }
];

/**
 * What survives in a game nobody is playing.
 *
 * Build and Discard are the two drag tools that issue *commands*, and there is no
 * seat to issue them from — a watched game answers every ownership test with
 * "not yours", so both would be buttons that did nothing. Hidden rather than
 * disabled: "a control is disabled, never removed" is about controls that
 * *flicker*, and this is fixed for the whole game (§28.6, the whole-seat
 * exception).
 */
const WATCH_TOOLS = new Set<Tool>(["turn"]);

/** One tile of the rail, ready to draw. */
export interface RailTool {
  id: Tool;
  label: string;
  hint: string;
  icon?: IconName;
  glyph?: string;
  /** The live tool. Exactly one of the offered tiles. */
  active: boolean;
  /** Why it is off, or null. An off tile's title is the whole of its job. */
  reason: string | null;
}

export interface RailInput {
  tool: Tool;
  /** Two machines are playing each other and nobody holds a seat. */
  watching: boolean;
}

/**
 * The rail, as a list.
 *
 * **Nothing here refuses any more.** The one tile that ever carried a reason
 * was the camera, off while the whole board fitted the viewport; the view moves
 * under every tool now, so there is nothing left to switch off. `reason`
 * survives because the next tool to need one wants the field, not a rewrite.
 */
export function railTools({ tool, watching }: RailInput): RailTool[] {
  return ENTRIES.filter((entry) => !watching || WATCH_TOOLS.has(entry.id)).map((entry) => ({
    ...entry,
    hint: TOOL_HINT[entry.id] ?? TOOL_HINT.turn,
    active: entry.id === tool,
    reason: null
  }));
}
